/**
 * TL;DR: Advanced product item component with quick shop and media features.
 * Features:
 * - Quick shop with modal or direct add-to-cart
 * - Rich media support (images, videos, lightbox)
 * - Swatch and variant management
 * - Dynamic content loading
 * - Responsive error handling
 * - Cart integration
 */

import { b as addItemById, a as contexts } from './cart-66092e7d.js';
import { d as dialog } from './modals-c8df3839.js';
import { E as EVENTS } from './events-29317bb6.js';
import { i as insertedMarkupFixes } from './inserted-markup-fixes-cdea8ec6.js';
import './svg-images-loaded-7454cac3.js';

class ProductItem extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      loader: "[js-loader]",
      productBlockVendor: "[js-product-block-vendor]",
      productBlockTitle: "[js-product-block-title]",
      productBlockPrice: "[js-product-block-price]",
      productBlockVariantSelector: "[js-product-block-variant-selector]",
      productBlockQuantitySelector: "[js-product-block-quantity-selector]",
      productBlockBuyButtons: "[js-product-block-buy-buttons]",
      productBlockStoreAvailability: "[js-store-availability-container]",
      productBlockCustomOption: "[js-product-block-custom-option]",
      productBlockApps: ".shopify-app-block",
      productDetails: "[js-product-details]",
      productMedia: "[js-product-media]",
      productMediaBackgroundVideo: "[js-background-video]",
      productMediaImage: "[js-media-image]",
      productMediaItem: "[js-media-item-wrapper]",
      productMediaLightboxLink: "[js-media-lightbox-link]",
      productMediaLightboxTrigger: "[js-media-lightbox-trigger]",
      productMediaThumbnailItem: "[js-product-thumbnail-item]",
      productMediaWrapper: "[js-product-media-wrapper]",
      productMediaTemplate: "[js-quick-view-media-template]",
      productMediaItemLink: ".product-media__item a",
      productInner: "[js-product-inner]",
      productSection: "[js-product-section]",
      quickShopButton: "[js-quick-shop-button]",
      quickShopButtonWrapper: "[js-quick-shop-button-wrapper]",
      quickShopError: "[js-quick-shop-error]",
      quickViewContent: "[js-quick-view-content]",
      quickViewFullDetailsTemplate: "[js-quick-view-full-details-template]",
      quickViewTemplate: "[js-quick-view-template]",
      quickShoppingWrapper: "[js-quick-shopping-wrapper]",
      swatchImages: "[js-product-item-swatch-image]",
      swatches: "[js-swatch]",
      swatchOverflowButton: "[js-product-listing-swatch-overflow]",
    };

    this.classes = {
      hasError: "has-error",
      active: "active",
      adding: "adding",
      quickViewProductInner: "product-inner--quick-view",
      success: "success",
      swatchActive: "swatch-active",
      selectedSwatch: "swatch--selected",
    };
  }

  connectedCallback() {
    this.controller = new AbortController();
    this.quickShopButton = this.querySelector(this.selectors.quickShopButton);
    this.quickShopButtonWrapper = this.querySelector(
      this.selectors.quickShopButtonWrapper,
    );
    this.swatchImages = this.querySelectorAll(this.selectors.swatchImages);
    this.swatches = this.querySelectorAll(this.selectors.swatches);
    this.swatchOverflowButton = this.querySelector(
      this.selectors.swatchOverflowButton,
    );

    // Initialize quick shop functionality
    if (this.quickShopButton) {
      this.quickShopType = this.quickShopButton.getAttribute("data-type");
      this.hasVariants =
        this.quickShopButton.getAttribute("data-has-variants") === "true";

      this.quickShoppingWrapper = this.querySelector(
        this.selectors.quickShoppingWrapper,
      );
      this.quickViewTemplate = this.querySelector(
        this.selectors.quickViewTemplate,
      );

      // TODO: what happens if the same product is rendered in two product items on a page?
      this.quickViewID = `quick-view-${this.getAttribute("data-product-id")}`;

      // Listen for click on quick shop button
      this.quickShopButton.addEventListener(
        "click",
        this.handleQuickShopClick.bind(this),
        { signal: this.controller.signal },
      );

      // Listen for mouseover on quick shop button
      // TODO: see note in handleQuickShopMouseover
      // this.quickShopButton.addEventListener(
      //   "mouseover",
      //   this.handleQuickShopMouseover.bind(this),
      //   { once: true },
      // )

      // If quick shop type is "quick-add" and the product has no variants, add
      // listeners for:
      //   - quick-add success
      //   - quick-add failure
      //   - cart update (clear error)
      if (this.quickShopType === "quick-add" && !this.hasVariants) {
        this.addEventListener(
          EVENTS.QUICK_ADD_SUCCESS,
          this.handleQuickAddSuccess.bind(this),
          { signal: this.controller.signal },
        );

        this.quickShopError = this.querySelector(this.selectors.quickShopError);

        this.addEventListener(
          EVENTS.QUICK_ADD_ERROR,
          this.handleQuickAddError.bind(this),
          { signal: this.controller.signal },
        );

        document.addEventListener(
          EVENTS.CART_CHANGED,
          this.handleCartUpdate.bind(this),
          { signal: this.controller.signal },
        );
      }

      // Listen for "quick add" error to show message and update button state
      this.quickShopButton.addEventListener(
        EVENTS.QUICK_ADD_ERROR,
        this.handleQuickAddError.bind(this),
        { signal: this.controller.signal },
      );
    }

    // If we have swatches, add click listeners to each swatch
    this.swatches.forEach((swatch) => {
      swatch.addEventListener("click", this.handleSwatchClick.bind(this), {
        signal: this.controller.signal,
      });
    });

    // If we have swatch overflow, add a click listener
    if (this.swatchOverflowButton && this.quickShopButton) {
      this.swatchOverflowButton.addEventListener(
        "click",
        this.handleSwatchOverflowClick.bind(this),
        { signal: this.controller.signal },
      );
    }
  }

  handleQuickShopClick() {
    if (this.quickShopType === "quick-add" && !this.hasVariants) {
      this.handleQuickAdd();
    } else {
      this.openQuickView();
    }
  }

  // handleQuickShopMouseover() {
  // TODO: this does not work currently, the browser always refetches on click, probably becase
  //  the URL has cache-control:no-cache, no-store - it is possible that this worked on the
  //  section rendering API? if we want to do this here we could fetch on mouseover and store
  //  response here in the custom element, and reuse it if it is not stale upon actual click.
  //   Also would be good to do on variant change in modal
  //   // If the quick view experience can be opened, preload the associated product page
  //   if (
  //     (this.quickShopType === "quick-add" && this.hasVariants) ||
  //     this.quickShopType === "quick-view"
  //   ) {
  //     // this.getQuickViewProductSection()
  //     const prefetchLink = document.createElement("LINK")

  //     prefetchLink.setAttribute("rel", "prefetch")
  //     prefetchLink.setAttribute("href", this.getAttribute("data-product-url"))
  //     prefetchLink.setAttribute("as", "fetch")
  //     prefetchLink.setAttribute("crossorigin", "use-credentials")
  //     prefetchLink.setAttribute("type", "text/html")
  //     document.head.appendChild(prefetchLink)
  //   }
  // }

  handleQuickAdd() {
    this.quickShopButtonWrapper.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_STARTED),
    );

    addItemById(
      this.quickShopButton.dataset.variantId,
      1,
      contexts.productItem,
      this,
    );
  }

  handleQuickAddSuccess() {
    this.quickShopButtonWrapper.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_COMPLETED),
    );
    this.classList.remove(this.classes.hasError);
  }

  handleQuickAddError(event) {
    this.quickShopButtonWrapper.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_INCOMPLETED),
    );

    this.classList.add(this.classes.hasError);
    this.quickShopError.innerText = event.detail.errorMessage;
  }

  handleCartUpdate() {
    this.classList.remove(this.classes.hasError);
  }

  async openQuickView() {
    if (!this.quickViewTemplate) {
      console.debug("openQuickView called when template doesn't exist");
      return
    }

    this.quickShopButtonWrapper.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_STARTED),
    );

    const templateContent = this.quickViewTemplate.content.cloneNode(true);
    const quickViewModal = templateContent.querySelector("dialog");

    this.quickShoppingWrapper.appendChild(templateContent);

    // Create the quick-view-modal if not yet created
    // TODO: can this be a shared modal across products?
    if (
      !window.flu.dialogs[`quick-view-${this.getAttribute("data-product-id")}`]
    ) {
      dialog(quickViewModal);
    }

    this.quickViewContent = this.querySelector(this.selectors.quickViewContent);

    // TODO consider breaking this out into something like convertProductPageToQuickView
    const productSection = await this.getQuickViewProductSection();
    const productSectionElement = productSection.cloneNode(false);
    const productSectionInner = productSection
      .querySelector(this.selectors.productInner)
      .cloneNode(false);

    // Note: Quickview specific media markup in a template tag on the product page:
    // Because quick view is not compatable with with photoswipe lightbox (z-index vs dialog stacking context)
    // we need a lightbox-less version of the media here so that when we fetch the product page content for QV
    // we have markup that will work (inline video player etc.).  Originally we tried to fetch the markup as is
    // as manipulate it to support inline playing but there is too much complexity to doing so.  Some improvements
    // here could be to only do this if lightbox is enabled and use the default content if not.
    const productMediaTemplate = productSection.querySelector(
      this.selectors.productMediaTemplate,
    );
    const productMediaFragment = productMediaTemplate.content;

    this.quickViewFullDetailsLink = productSection
      .querySelector(this.selectors.quickViewFullDetailsTemplate)
      ?.content.cloneNode(true);

    const productDetails = productSection
      .querySelector(this.selectors.productDetails)
      .cloneNode(false);
    const vendor = productSection.querySelector(
      this.selectors.productBlockVendor,
    );
    const title = productSection.querySelector(this.selectors.productBlockTitle);
    const price = productSection.querySelector(this.selectors.productBlockPrice);
    const variantSelector = productSection.querySelector(
      this.selectors.productBlockVariantSelector,
    );

    const quantitySelector = productSection.querySelector(
      this.selectors.productBlockQuantitySelector,
    );

    const customOptionBlocks = productSection.querySelectorAll(
      this.selectors.productBlockCustomOption,
    );

    // App blocks like shopify Subscription block does not work reliably, removing these for now
    // const appBlocks = productSection.querySelectorAll(
    //   this.selectors.productBlockApps,
    // )

    const buyButtons = productSection.querySelector(
      this.selectors.productBlockBuyButtons,
    );

    productSectionElement.setAttribute(
      "data-section-type",
      "quick-view-product",
    );

    // Strip all inline styles from product section otherwise we will be setting color scheme vars
    // here which conflict with with modal vars.  If we ever have PDP vars on here that are relevant
    // outside of color settings, we will need to do something different here
    productSectionElement.style = "";

    productSectionInner.classList.add(this.classes.quickViewProductInner);

    let buyButtonBottomSpacing = "var(--spacing-400)";

    if (vendor) {
      productDetails.appendChild(vendor);
      buyButtonBottomSpacing = vendor.style.getPropertyValue("--bottom-spacing");
    }

    if (title) {
      const titleLink = document.createElement("a");
      titleLink.setAttribute("href", this.getAttribute("data-product-url"));
      titleLink.innerHTML = title.innerHTML;
      title.replaceChildren(titleLink);

      productDetails.appendChild(title);
      buyButtonBottomSpacing = title.style.getPropertyValue("--bottom-spacing");
    }

    if (price) {
      productDetails.appendChild(price);
      buyButtonBottomSpacing = price.style.getPropertyValue("--bottom-spacing");
    }

    if (this.quickViewFullDetailsLink) {
      productDetails.appendChild(this.quickViewFullDetailsLink);
      buyButtonBottomSpacing = "var(--spacing-400)";
    }

    if (variantSelector) {
      // To avoid glitches with dialogs-inside-dialogs (close button closing both)
      // we detect if there is a dialog in the variant selector and later inject
      // it after the main quick-view dialog
      const variantSelectorSubDialog = variantSelector
        .querySelector("dialog")
        ?.cloneNode(true);

      if (variantSelectorSubDialog) {
        variantSelector.querySelector("dialog").remove();
      }

      productDetails.appendChild(variantSelector);
      buyButtonBottomSpacing =
        variantSelector.style.getPropertyValue("--bottom-spacing");

      if (variantSelectorSubDialog) {
        const variantSelectorSubDialogId =
          variantSelectorSubDialog.getAttribute("data-fluco-dialog");

        // To be safe, lets remove any previous version of this dialog before
        // reinserting/intiting.  TODO: look into better options for this

        if (window.flu.dialogs[variantSelectorSubDialogId]) {
          window.flu.dialogs[variantSelectorSubDialogId][1].destroy({
            deregister: true,
          });
        }

        document
          .querySelector(
            `dialog[data-fluco-dialog='${variantSelectorSubDialogId}']`,
          )
          ?.remove();

        quickViewModal.after(variantSelectorSubDialog);
        dialog(variantSelectorSubDialog);
      }
    }

    if (quantitySelector) {
      productDetails.appendChild(quantitySelector);
      buyButtonBottomSpacing =
        quantitySelector.style.getPropertyValue("--bottom-spacing");
    }

    if (customOptionBlocks.length) {
      customOptionBlocks.forEach((element) => {
        productDetails.appendChild(element);
      });
    }

    // App blocks like shopify Subscription block does not work reliably, removing these for now
    // if (appBlocks.length) {
    //   appBlocks.forEach((element) => {
    //     productDetails.appendChild(element)
    //   })
    // }

    if (buyButtons) {
      // We don't want the store availability content, so remove it
      buyButtons
        .querySelector(this.selectors.productBlockStoreAvailability)
        ?.remove();
      buyButtons.style = `--bottom-spacing: ${buyButtonBottomSpacing};`;

      productDetails.appendChild(buyButtons);
    }

    productSectionInner.appendChild(productMediaFragment);
    productSectionInner.appendChild(productDetails);
    productSectionElement.appendChild(productSectionInner);
    insertedMarkupFixes(productSectionElement);

    window.dispatchEvent(
      new CustomEvent(EVENTS.MODAL_POPUP_OPEN(this.quickViewID)),
    );

    this.quickViewContent.replaceChildren(productSectionElement);

    this.quickShopButtonWrapper.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_INCOMPLETED),
    );
  }

  async getQuickViewProductSection() {
    const productPage = await fetch(`${this.getAttribute("data-product-url")}`);

    if (productPage) {
      const productPageText = await productPage.text();
      const productDocument = new DOMParser().parseFromString(
        productPageText,
        "text/html",
      );

      return productDocument.querySelector(this.selectors.productSection)
    }
  }

  handleSwatchClick(event) {
    const swatch = event.currentTarget;

    // If the swatch is already selected, deselect it and return
    if (swatch.classList.contains(this.classes.selectedSwatch)) {
      swatch.classList.remove(this.classes.selectedSwatch);
      this.classList.remove(this.classes.swatchActive);

      for (let i = 0; i < this.swatchImages.length; i++) {
        const swatchImage = this.swatchImages[i];

        if (swatchImage.dataset.swatchValue === swatch.dataset.value) {
          swatchImage.classList.remove(this.classes.active);
          break
        }
      }

      return
    }

    // Hide currently active swatch image
    this.swatchImages.forEach((swatchImage) => {
      swatchImage.classList.remove(this.classes.active);
    });

    // Deselect all swatches
    this.swatches.forEach((swatch) => {
      swatch.classList.remove(this.classes.selectedSwatch);
    });

    // Select the current swatch and show the associated image
    swatch.classList.add(this.classes.selectedSwatch);

    let foundSwatchImage = false;

    for (let i = 0; i < this.swatchImages.length; i++) {
      const swatchImage = this.swatchImages[i];

      if (swatchImage.dataset.swatchValue === swatch.dataset.value) {
        this.classList.add(this.classes.swatchActive);
        swatchImage.classList.add(this.classes.active);
        foundSwatchImage = true;
        break
      }
    }

    if (!foundSwatchImage) {
      this.classList.remove(this.classes.swatchActive);
    }
  }

  handleSwatchOverflowClick() {
    this.openQuickView();
  }

  disconnectedCallback() {
    this.controller.abort();
  }
}

if (!customElements.get("product-item")) {
  customElements.define("product-item", ProductItem);
}
